<template>
  <div>
    <!-- ===== LOADING ===== -->
    <v-progress-circular
        v-if="isLoading"
        :size="70"
        :width="7"
        color="primary"
        indeterminate
        class="centered loading"
    />

    <!-- ===== CONTENT ===== -->
    <div :class="{ 'blur--content': isLoading }">
      <h1 class="text-center">Customer Age Group Report</h1>

      <ReportParameters
          v-if="reportRequest"
          :request="reportRequest"
      />

      <!-- ===== SUMMARY SECTION ===== -->
      <div class="mt-5">

        <v-btn
            text
            class="btn-back"
            @click="$router.push({ name: 'AgeGroup' })"
        >
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>

        <div class="age-stat-card">
          <v-card class="age-card pa-4" color="grey lighten-4">
            <div class="d-flex justify-space-between align-center">
              <h3 class="title">Age Statistics</h3>
              <span class="note">(Not included in export)</span>
            </div>

            <div class="card-body pa-0">
              <div class="stat-item">
                <span class="label">Mean Age:</span>
                <span class="value">{{ formatNumber2(meanAge) }}</span>
              </div>

              <div class="stat-item">
                <span class="label">Median Age:</span>
                <span class="value">{{ formatNumber2(medianAge) }}</span>
              </div>
            </div>

            <!-- Export Button -->

          </v-card>
          <div class="d-flex justify-end mt-6">
            <button
                class="btn btn-dark"
                :disabled="listData.length <= 0"
                @click="exportExcel"
                style=" display: inline-flex;"
            >
              <v-icon left size="18" color="white">mdi-file-excel</v-icon>
              Export to Excel
            </button>
          </div>
          <!-- ===== TABLE ===== -->
          <MetricTable
              v-if="displayData.length > 0"
              :fields="fields"
              :list-data="displayData"
              :is-retention-metric="false"
          />

          <!-- ===== EMPTY MESSAGE ===== -->
          <div v-else-if="message">
            <h3 class="text-center mt-5">{{ message }}</h3>
          </div>
        </div>
      </div>

    </div>

    <!-- ===== ERROR DIALOG ===== -->
    <v-dialog v-model="showDialog" width="500" persistent>
      <div class="content-dialog">
        <h5 class="p-5 failureMessage">{{ failureMessage }}</h5>
        <div class="text-center pb-4">
          <button class="btn-back" @click="showDialog = false">ปิด</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import fields from "@/json/reports/fields_age_group.json";
import ReportService from "services/api/report.service";
import MetricTable from "@/views/Reports/MetricsUtils/MetricTable.vue";
import createAgeGroupWorkbook from "@/views/Reports/createAgeGroupWorkbook";
import ReportParameters from "@/views/Reports/AgeGroup/ReportParameters.vue";

export default {
  components: {
    MetricTable,
    ReportParameters,
  },

  data() {
    return {
      fileName: `age_group_metrics_${moment().format("YYYYMMDDHHmmss")}`,
      listData: [],
      fields,
      filteredData: {
        brand: null,
        date: null,
        isIncludeVVIP: false,
        channel: null,
        sources: null,
      },
      isLoading: false,
      message: null,
      failureMessage: null,
      showDialog: false,
      reportRequest: null,
      meanAge: null,
      medianAge: null,

      totalCustomer: null,
      totalAmount: null,
      totalTransaction: null,
      totalQuantity: null,

      totalArpu: null,
      totalAov: null,
      totalIpt: null,
      totalAof: null,
      totalAus: null,
    };
  },

  mounted() {
    this.getAgeGroupMetrics();
  },

  computed: {
    displayData() {
      return this.tableData.map((row) => {
        const formattedRow = { ...row };

        this.fields.forEach((field) => {
          const value = formattedRow[field.key];
          if (typeof value !== "number") return;

          switch (field.format) {
            case "integer":
              formattedRow[field.key] = value.toLocaleString("en-US");
              break;

            case "number":
              formattedRow[field.key] = value.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              break;

            case "currency":
              formattedRow[field.key] = value.toLocaleString("en-US", {
                style: "currency",
                currency: "THB",
                minimumFractionDigits: 2,
              });
              break;

            case "percent":
              formattedRow[field.key] = `${value.toFixed(2)}%`;
              break;
          }
        });

        return formattedRow;
      });
    },
    summaryRow() {
      if (!this.listData.length) return null;

      return {
        ageGroup: "Total",
        _rowClass: "summary-row",

        customerCount: this.totalCustomer ?? "",
        totalSalesAmount: this.totalAmount ?? "",
        transactionCount: this.totalTransaction ?? "",
        totalItems: this.totalQuantity ?? "",

        arpu: this.totalArpu ?? "",
        aov: this.totalAov ?? "",
        ipt: this.totalIpt ?? "",
        aof: this.totalAof ?? "",
        aus: this.totalAus ?? "",
      };
    },

    tableData() {
      if (!this.summaryRow) return this.listData;
      return [...this.listData, this.summaryRow];
    },
  },

  methods: {
    async getAgeGroupMetrics() {
      this.isLoading = true;
      const param = {
        processId: this.$route.query.processId,
      };

      try {
        const res = await ReportService.getAgeGroupReportResult(param);
        const result = res.data;

        this.listData = result.data || [];
        this.message = result.message || null;
        this.meanAge = result.meanAge;
        this.medianAge = result.medianAge;

        this.totalCustomer = result.totalCustomer;
        this.totalAmount = result.totalAmount;
        this.totalTransaction = result.totalTransaction;
        this.totalQuantity = result.totalQuantity;

        this.totalArpu = result.totalArpu;
        this.totalAov = result.totalAov;
        this.totalIpt = result.totalIpt;
        this.totalAof = result.totalAof;
        this.totalAus = result.totalAus;

        if (result.request) {
          this.reportRequest = result.request;

          const req = result.request;
          this.filteredData = {
            brand: req.brandCode,
            date: req.date,
            isIncludeVVIP: req.includeV,
            channel: req.channel,
            sources: req.sources,
          };
        }
      } catch (err) {
        const errRes = err.response;
        this.failureMessage =
            errRes?.data?.message ||
            "An error occurred, please try again later.";
        this.showDialog = true;
      } finally {
        this.isLoading = false;
      }
    },

    exportExcel() {
      createAgeGroupWorkbook.createExcel(this.tableData, this.fields, this.fileName);
    },

    formatNumber2(value) {
      if (value == null || Number.isNaN(value)) {
        return "-";
      }
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

  },
};
</script>

<style lang="scss" scoped>
.report-ag {
  padding: 16px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.centered {
  position: absolute;
  top: 45%;
  left: 50%;
}

.loading {
  z-index: 10;
}

.blur--content {
  filter: blur(2px);
  pointer-events: none;
}

.button-group {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.summary-row {
  font-weight: bold;
  background-color: #f3f3f3;
  border-top: 2px solid #000;
}

.report-summary {
  background: #f7f7df;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
}

.btn-back {
  background: #fff;
  border: 1px solid #ddd;
  padding: 6px 14px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 16px;
  color: #000;
}

.age-stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #000;

  h3 {
    margin: 0;
    font-size: 18px;
  }

  .note {
    font-size: 13px;
    color: #6c757d;
    font-style: italic;
  }
}

.card-body {
  display: flex;
  gap: 40px;
  color: #000;
}

.stat-item {
  .label {
    font-size: 14px;
    color: #6c757d;
    margin-right: 6px;
  }

  .value {
    font-size: 16px;
    font-weight: 600;
  }
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.age-card {
  border-radius: 12px;
}

.title {
  font-weight: 600;
}

.note {
  font-size: 0.85rem;
  color: #6b7280;
  font-style: italic;
}

.stat {
  font-size: 1rem;
}

.label {
  margin-right: 6px;
  color: #374151;
}

.value {
  font-size: 1.1rem;
}

::v-deep tr:last-child{
  background-color: #f3f4f6;
}

::v-deep tr:last-child td {
  font-weight: 600;
}

</style>
